import { LitElement, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

export type PlanChecklistStep = {
  text: string;
  status: string;
};

const STATUS_ICON: Record<string, string> = {
  pending: "⬚",
  in_progress: "◐",
  done: "✓",
  skipped: "⊘",
};

/**
 * <ig-plan-checklist> — the agent's live plan, derived client-side from the
 * newest update_plan tool call visible in the chat stream (design D13).
 *
 * Renders a compact card with one line per step; done/skipped steps are
 * dimmed. Light-DOM styling via CSS tokens and the shared `card`/`row`/`muted`
 * utility classes.
 */
@customElement("ig-plan-checklist")
export class PlanChecklist extends LitElement {
  protected createRenderRoot() {
    return this;
  }

  @property({ type: String }) planTitle = "";
  @property({ type: Array }) steps: PlanChecklistStep[] = [];

  render() {
    if (!this.steps.length) {
      return nothing;
    }
    const done = this.steps.filter((s) => s.status === "done" || s.status === "skipped").length;

    return html`
      <div class="plan-checklist card" style="padding:10px 14px;margin:8px 0;overflow-wrap:anywhere;">
        <div class="row" style="justify-content:space-between;">
          <strong>${this.planTitle || "Plan"}</strong>
          <span class="muted" style="font-size:0.85em;">${done}/${this.steps.length}</span>
        </div>
        <ul style="list-style:none;margin:6px 0 0;padding:0;max-height:min(30vh,320px);overflow-y:auto;">
          ${this.steps.map((s) => {
            const settled = s.status === "done" || s.status === "skipped";
            const iconColor = s.status === "in_progress" ? "var(--accent, #c084fc)" : "inherit";
            return html`<li style="opacity:${settled ? 0.55 : 1};padding:1px 0;">
              <span style="display:inline-block;width:1.5em;color:${iconColor};">${STATUS_ICON[s.status] ?? "⬚"}</span>${s.text}
            </li>`;
          })}
        </ul>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ig-plan-checklist": PlanChecklist;
  }
}
